import type { Editor } from "./canvas";
import type { TLShape, TLImageShape } from "@tldraw/tlschema";
import type { Sheet, Trace } from "../shared/schema";
import { displayObjectName } from "./object-name";
export type DrawingSource = Sheet | TLImageShape | Trace;
export function isSource(s: TLShape | undefined): s is DrawingSource {
  return !!s && ["sheet", "image", "trace"].includes(s.type);
}
export function sourceName(s: DrawingSource) {
  return s.type === "image"
    ? displayObjectName(s.meta.name || s.props.altText)
    : s.props.name;
}
export function scaleSource(
  editor: Editor,
  shape: TLShape | null | undefined,
): DrawingSource | undefined {
  if (!shape) return;
  if (isSource(shape)) return shape;
  return shape.parentId.startsWith("shape:")
    ? scaleSource(editor, editor.getShape(shape.parentId as TLShape["id"]))
    : undefined;
}
export function sourceMeters(
  editor: Editor,
  source: DrawingSource | undefined,
): number {
  if (!source) return 0;
  if (source.type === "trace") {
    if (source.parentId.startsWith("page:")) {
      const scale = Number(source.meta.metersPerUnit);
      return !source.meta.scaleReview && Number.isFinite(scale) && scale > 0
        ? scale
        : 0;
    }
    return sourceMeters(
      editor,
      scaleSource(editor, editor.getShape(source.parentId as TLShape["id"])),
    );
  }
  if (source.type === "sheet") return source.props.metersPerUnit;
  if (source.meta.scaleReview) return 0;
  const value = Number(source.meta.metersPerUnit ?? 0),
    width = Number(source.meta.calibrationWidth ?? source.props.w);
  return value > 0 && width > 0 ? (value * width) / source.props.w : 0;
}
export function setSourceScale(
  editor: Editor,
  s: DrawingSource,
  factor: number,
  label: string,
) {
  if (s.type === "trace" && !s.parentId.startsWith("page:")) {
    const base = scaleSource(
      editor,
      editor.getShape(s.parentId as TLShape["id"]),
    );
    if (base) setSourceScale(editor, base, factor, label);
    return;
  }
  editor.markHistoryStoppingPoint("Set source scale");
  if (s.type === "sheet")
    editor.updateShape({
      id: s.id,
      type: s.type,
      props: { metersPerUnit: factor, calibration: label },
    });
  else
    editor.updateShape({
      id: s.id,
      type: s.type,
      meta: {
        ...s.meta,
        metersPerUnit: factor,
        calibrationWidth: s.props.w,
        calibration: label,
        scaleReview: false,
      },
    });
}
export function sourceAt(
  editor: Editor,
  p: { x: number; y: number },
): DrawingSource | undefined {
  const selected = editor.getOnlySelectedShape(),
    preferred = scaleSource(editor, selected);
  const contains = (s: DrawingSource) => {
    const q = editor.getPointInShapeSpace(s, p);
    return (
      q.x >= 0 &&
      q.y >= 0 &&
      q.x <= s.props.w &&
      q.y <= s.props.h &&
      !editor.isShapeOrAncestorLocked(s) &&
      !editor.isShapeHidden(s)
    );
  };
  if (preferred && contains(preferred)) return preferred;
  const found = editor
    .getCurrentPageShapesSorted()
    .filter(isSource)
    .reverse()
    .filter(contains);
  // Explicitly selected sources disambiguate overlaps. A sheet underneath an
  // image is its layout container, never an implicit calibration fallback.
  const objects = found.filter((s) => s.type !== "sheet");
  if (objects.length > 1 && objects[0].parentId !== objects[1].id)
    throw new Error(
      "Select the drawing or trace you want to measure, then try again.",
    );
  return objects[0] ?? found[0];
}
